<script setup lang="ts">
import ArrowRight from '@/assets/icon/arrow-right.svg'
import type { WebHomeIndustryNewsItem } from '~/server/home'

type IndustryNewsSlide = {
  id: string
  image: string
  title: string
  detailUrl: string
}

const DEFAULT_NEWS_IMAGES = [
  'https://images.unsplash.com/photo-1621416894569-0f39ed31d247?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1642104704074-907c0698cbd9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1621761191319-c6fb62004040?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
]

const AUTOPLAY_INTERVAL = 300000

const { locale, localePath, t } = useAppI18n()

const matomo = useMatomo()

const props = defineProps<{
  data?: WebHomeIndustryNewsItem[]
}>()

const currentIndex = ref(0)
const autoplayPaused = ref(false)
let autoplayTimer: ReturnType<typeof setInterval> | null = null
let stopSlidesWatch: (() => void) | null = null

const newsItems = computed<WebHomeIndustryNewsItem[]>(() => (Array.isArray(props.data) ? props.data : []))
const isRtl = computed(() => locale.toLowerCase().startsWith('ar'))

const newsSlides = computed<IndustryNewsSlide[]>(() => {
  return newsItems.value
    .map((item, index) => {
      const title = item.title?.trim() || item.intro?.trim() || ''

      if (!title) {
        return null
      }

      return {
        id: item.code || `industry-news-slide-${index + 1}`,
        image: item.imgurl || DEFAULT_NEWS_IMAGES[index % DEFAULT_NEWS_IMAGES.length] || DEFAULT_NEWS_IMAGES[0],
        title,
        urlPath: item.urlPath || '',
        detailUrl: item.urlPath ? localePath(`/news/${item.urlPath}`) : '',
      } satisfies IndustryNewsSlide
    })
    .filter((item): item is IndustryNewsSlide => Boolean(item?.detailUrl))
})
// 轨道首尾包含克隆项，因此真实第一项从索引 1 开始。
const trackIndex = ref(newsSlides.value.length > 1 ? 1 : 0)
const isTransitionEnabled = ref(false)
const isSliding = ref(false)

const carouselSlides = computed<IndustryNewsSlide[]>(() => {
  if (newsSlides.value.length <= 1) {
    return newsSlides.value
  }

  const firstSlide = newsSlides.value[0]
  const lastSlide = newsSlides.value.at(-1)
  return firstSlide && lastSlide ? [lastSlide, ...newsSlides.value, firstSlide] : newsSlides.value
})

const currentSlide = computed(() => {
  return newsSlides.value[currentIndex.value] ?? newsSlides.value[0] ?? null
})

const totalSlides = computed(() => newsSlides.value.length)
const trackStyle = computed(() => ({
  transform: `translate3d(${isRtl.value ? '' : '-'}${trackIndex.value * 100}%, 0, 0)`,
  transition: isTransitionEnabled.value ? undefined : 'none',
}))

const stopAutoplay = () => {
  if (!autoplayTimer) {
    return
  }

  clearInterval(autoplayTimer)
  autoplayTimer = null
}

const startAutoplay = () => {
  stopAutoplay()

  if (totalSlides.value <= 1) {
    return
  }

  autoplayTimer = setInterval(() => {
    if (autoplayPaused.value) {
      return
    }

    moveSlide(1)
  }, AUTOPLAY_INTERVAL)
}

// 同步更新真实页码与带克隆项的轨道索引，并防止连续动画互相覆盖。
const moveSlide = (step: -1 | 1) => {
  if (totalSlides.value <= 1 || isSliding.value) {
    return
  }

  isTransitionEnabled.value = true
  isSliding.value = true
  currentIndex.value = (currentIndex.value + step + totalSlides.value) % totalSlides.value
  trackIndex.value += step
}

const goToPrevSlide = () => {
  moveSlide(-1)
  matomo.trackEvent('home_new_switch', {})
}

const goToNextSlide = () => {
  matomo.trackEvent('home_new_switch', {})
  moveSlide(1)
}

const toNew = () => {
  matomo.trackEvent('home_new_details', {
    af_name: currentSlide?.value?.title,
    af_code: currentSlide.value?.id,
  })
}
// 到达克隆项后关闭动画，静默复位到对应的真实幻灯片。
const handleTrackTransitionEnd = () => {
  if (trackIndex.value === 0) {
    isTransitionEnabled.value = false
    trackIndex.value = totalSlides.value
  } else if (trackIndex.value === totalSlides.value + 1) {
    isTransitionEnabled.value = false
    trackIndex.value = 1
  }

  isSliding.value = false
}

onMounted(() => {
  stopSlidesWatch = watch(
    totalSlides,
    (nextTotal) => {
      currentIndex.value = 0
      trackIndex.value = nextTotal > 1 ? 1 : 0
      isTransitionEnabled.value = false
      isSliding.value = false

      if (nextTotal === 0) {
        stopAutoplay()
        return
      }

      startAutoplay()
    },
    { immediate: true },
  )
})

onBeforeUnmount(() => {
  stopSlidesWatch?.()
  stopSlidesWatch = null
  stopAutoplay()
})
</script>

<template>
  <div class="industry-news-card">
    <div class="card-header">
      <h2 class="title">{{ t('82492') }}</h2>
      <!-- <button type="button" class="arrow-icon mr-[6px]" aria-label="View more news">
        <ArrowRight />
      </button> -->
    </div>

    <NuxtLink
      v-if="currentSlide"
      :to="localePath({ name: 'news-detail', params: { slug: currentSlide?.urlPath } })"
      class="news-content"
      :dir="isRtl ? 'rtl' : 'ltr'"
      target="_blank"
      rel="noopener noreferrer"
      @mouseenter="autoplayPaused = true"
      @mouseleave="autoplayPaused = false"
      @click="toNew"
    >
      <div class="news-track" :style="trackStyle" @transitionend.self="handleTrackTransitionEnd">
        <div v-for="(slide, slideIndex) in carouselSlides" :key="`${slide.id}-${slideIndex}`" class="news-slide">
          <CommonImage
            :src="slide.image"
            alt="News Image"
            class="news-image"
            width="100%"
            height="100%"
            loading="eager"
            object-fit="cover"
          />
          <div class="news-overlay"></div>
          <div class="news-info">
            <p class="news-title">{{ slide.title }}</p>
          </div>
        </div>
      </div>
      <span class="pagination">{{ currentIndex + 1 }} / {{ totalSlides }}</span>
      <div v-if="totalSlides > 1" class="news-nav">
        <button
          type="button"
          class="news-nav-btn is-prev"
          aria-label="Previous news slide"
          @click.stop.prevent="goToPrevSlide"
        >
          <ArrowRight />
        </button>
        <button
          type="button"
          class="news-nav-btn is-next"
          aria-label="Next news slide"
          @click.stop.prevent="goToNextSlide"
        >
          <ArrowRight />
        </button>
      </div>
    </NuxtLink>
  </div>
</template>

<style scoped>
.industry-news-card {
  width: 348px;
  height: 308px;
  padding: 8px 24px 24px 24px;
  border-radius: 16px;
  border: 1px solid rgba(11, 18, 40, 0.14);
  background-color: #ffffff;
  box-sizing: border-box;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 16px;
  margin-bottom: 20px;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
}

.title {
  font-family: Outfit, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.arrow-icon {
  color: #8c8c8c;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
}

.news-content {
  display: block;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  width: 300px;
  height: 200px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;

  &:hover .news-nav-btn {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
}

.news-track {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.35s ease;
  will-change: transform;
}

.news-slide {
  position: relative;
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
}

.news-image {
  width: 100% !important;
  height: 100% !important;
  display: block;
  vertical-align: middle;
}

.news-image :deep(.main-image) {
  width: 100% !important;
  height: 100% !important;
}

.news-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60%;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%);
  pointer-events: none;
  z-index: 1;
}

.news-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  padding-inline-end: 56px;
  z-index: 2;
}

.news-title {
  color: #ffffff;
  font-family: Outfit, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin: 0;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pagination {
  position: absolute;
  inset-inline-end: 16px;
  bottom: 12px;
  z-index: 2;
  color: #ffffff;
  font-size: 12px;
  font-family: Outfit, sans-serif;
  opacity: 0.9;
  white-space: nowrap;
}

.news-nav {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
}

.news-nav-btn {
  position: relative;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 999px;
  background: var(--color-background-neutral, rgba(5, 12, 31, 0.46));
  backdrop-filter: blur(10px);
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transform: translateY(4px);
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 0.25s ease,
    transform 0.25s ease,
    background-color 0.25s ease;
}

.news-nav-btn:hover {
  background: var(--color-background-neutral, rgba(5, 12, 31, 0.46));
}

.news-nav-btn :deep(svg) {
  color: #ffffff;
}

.news-nav-btn :deep(path) {
  fill: #ffffff;
}

.news-nav-btn.is-prev {
  left: 0;
}

.news-nav-btn.is-prev :deep(svg) {
  transform: rotate(180deg);
}

.news-nav-btn.is-next {
  right: 0;
}

[dir='rtl'] .news-nav-btn.is-prev {
  right: 0;
  left: auto;
}

[dir='rtl'] .news-nav-btn.is-prev :deep(svg) {
  transform: rotate(0deg);
}

[dir='rtl'] .news-nav-btn.is-next {
  right: auto;
  left: 0;
}

[dir='rtl'] .news-nav-btn.is-next :deep(svg) {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {
  .news-track {
    transition-duration: 0.01ms;
  }
}

@media screen and (min-width: 800px) and (max-width: 1200px) {
  .industry-news-card {
    width: 100%;
  }

  .news-content {
    width: 100%;
  }
}

@media screen and (max-width: 799px) {
  .industry-news-card {
    width: 800px;
  }

  .news-content {
    width: 800px;
  }
}
</style>
